<script setup>
import { computed, ref, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * M3 text input with filled/outlined variants, validation, and error state.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Collecting text input with label, hint, and optional validation
 * @useWhen Collecting several lines of free text — set `multiline`, which is M3's variant of this
 * same field rather than a component of its own
 * @see https://quasar.dev/vue-components/input Quasar q-input API
 */
defineOptions({ name: 'HrTextField' });

const props = defineProps({
  /**
   * Visual container style derived from M3 text field variants.
   * @values filled, outlined
   */
  variant: {
    type: String,
    default: 'outlined',
    validator: (v) => ['filled', 'outlined'].includes(v),
  },
  /** Input value. */
  modelValue: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * Native input type. Declared because callers already rely on it and it changes what the field
   * accepts, not how it looks. Formats needing more than the browser gives get their own semantic
   * component instead, so the value they emit is a domain value rather than a formatted string.
   * @values text, textarea, number, email, tel, url, search
   * @avoidWhen A password field is wanted — use HrPasswordField, which adds the visibility toggle
   * @avoidWhen A time or duration is wanted — use HrTimeField or HrDurationField
   */
  type: {
    type: String,
    default: 'text',
  },
  /** Label text shown floating above the input. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Supporting text below the field: one sentence the reader needs to fill it in.
   * A longer or occasional explanation goes behind an HrIconHintBtn, never in a tooltip.
   */
  hint: {
    type: String,
    default: null,
  },
  /** Prevents interaction and dims the field. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Shows the value but prevents editing. */
  readonly: {
    type: Boolean,
    default: false,
  },
  /** Shows a loading spinner inside the field. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Shows a clear (×) button when the field has a value. */
  clearable: {
    type: Boolean,
    default: false,
  },
  /** Switches to the error color role. */
  error: {
    type: Boolean,
    default: false,
  },
  /** Error text shown below the field when `error` is true. */
  errorMessage: {
    type: String,
    default: null,
  },
  /**
   * Validation rules applied to the field value.
   */
  rules: {
    type: Array,
    default: null,
  },
  /**
   * When true, validation runs only after the first blur (not on every keystroke).
   */
  lazyRules: {
    type: [Boolean, String],
    default: false,
    validator: (v) => v === true || v === false || v === 'ondemand',
  },
  /**
   * Suppresses the reserved space below the field for hint/error messages.
   */
  hideBottomSpace: {
    type: Boolean,
    default: false,
  },
  /**
   * Reduces the field height to the M3 dense density level.
   */
  dense: {
    type: Boolean,
    default: false,
  },
  /**
   * Accepts several lines of free text, growing with its content.
   *
   * M3 models this as a variant of the text field rather than a separate component, because the
   * contract is identical either way — same label, hint, rules, error and density. So it is one
   * prop here, and it sets both halves of Quasar's version (`type` and `autogrow`) so a caller
   * cannot supply one without the other.
   *
   * An M3 multi-line field GROWS, so there is deliberately no fixed-height textarea: `rows` without
   * growth is not expressible through this component.
   */
  multiline: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props are any additional Quasar props that can be passed to the field for backwards
   * compatibility with the legacy theme. These props will be ignored when a design system theme
   * is active.
   * @type {Object} legacyProps
   * @deprecated This prop is only for backwards compatibility with the legacy theme and should be
   * removed once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted on input change; payload is the new string or number value. */
  'update:modelValue': null,
});

const qInputRef = ref(null);
defineExpose({
  /** Runs the field's validation rules; returns a boolean or a Promise resolving to one. */
  validate: (...args) => qInputRef.value?.validate(...args),
  /** Clears the field's validation state without running the rules. */
  resetValidation: () => qInputRef.value?.resetValidation(),
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const classes = computed(() => [
  'hr-text-field',
  `hr-text-field--${props.variant}`,
  props.dense && 'hr-text-field--dense',
]);

const effectiveProps = computed(() => {
  const base = {
    modelValue: props.modelValue,
    type: props.type,
    ...(props.label !== null && { label: props.label }),
    ...(props.hint !== null && { hint: props.hint }),
    ...(props.errorMessage !== null && { errorMessage: props.errorMessage }),
    disable: props.disable,
    readonly: props.readonly,
    loading: props.loading,
    clearable: props.clearable,
    error: props.error,
    dense: props.dense,
    ...(props.rules !== null && { rules: props.rules }),
    lazyRules: props.lazyRules,
    hideBottomSpace: props.hideBottomSpace,
    filled: props.variant === 'filled',
    outlined: props.variant === 'outlined',
    ...(props.multiline && { type: 'textarea', autogrow: true }),
  };

  if (hasTheme.value) {
    return {
      ...base,
      noErrorIcon: true,
      ...attrs,
    };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-input
    ref="qInputRef"
    v-bind="effectiveProps"
    :class="hasTheme ? classes : undefined"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template
      v-if="$slots.prepend"
      #prepend="slotProps"
    >
      <!-- @slot prepend Content placed before the field container (e.g. a leading icon). -->
      <slot
        name="prepend"
        v-bind="slotProps ?? {}"
      />
    </template>
    <template
      v-if="$slots.append"
      #append="slotProps"
    >
      <!-- @slot append Content placed after the field container (e.g. a trailing icon or clear button). -->
      <slot
        name="append"
        v-bind="slotProps ?? {}"
      />
    </template>
    <template
      v-if="$slots.hint"
      #hint="slotProps"
    >
      <!-- @slot hint Custom hint content rendered below the field. -->
      <slot
        name="hint"
        v-bind="slotProps ?? {}"
      />
    </template>
    <template
      v-if="$slots.loading"
      #loading="slotProps"
    >
      <!-- @slot loading Custom loading indicator replacing the default spinner. -->
      <slot
        name="loading"
        v-bind="slotProps ?? {}"
      />
    </template>
  </q-input>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

/* ── Common ─────────────────────────────────────────────────────────────── */

.hr-text-field :deep(.q-field__label) {
  color: var(--app-on-surface-variant);
  transition: color 150ms linear;
}

.hr-text-field :deep(.q-field__native),
.hr-text-field :deep(.q-field__input) {
  color: var(--app-on-surface);
  caret-color: var(--app-primary);
}

.hr-text-field :deep(.q-field__messages),
.hr-text-field :deep(.q-field__counter) {
  color: var(--app-on-surface-variant);
}

/* Focused: label shifts to primary */
.hr-text-field.q-field--focused :deep(.q-field__label) {
  color: var(--app-primary);
}

/* Error: label, caret, and supporting text use error color */
.hr-text-field.q-field--error :deep(.q-field__label) {
  color: var(--app-error);
}

.hr-text-field.q-field--error :deep(.q-field__messages) {
  color: var(--app-error);
}

.hr-text-field.q-field--error :deep(.q-field__native),
.hr-text-field.q-field--error :deep(.q-field__input) {
  caret-color: var(--app-error);
}

/* Leading / trailing icons */
.hr-text-field :deep(.q-field__prepend),
.hr-text-field :deep(.q-field__append) {
  color: var(--app-on-surface-variant);
}

/*
 * Prefix / suffix affix text — a currency symbol, a unit, a domain suffix.
 *
 * Quasar sets NO color on these at all: it styles their metrics and leaves the color to inherit
 * from the control, so without this they render in whatever ambient color the surface happens to
 * carry and visibly ignore the theme. They are siblings of `.q-field__native` rather than children,
 * so the input color above does not reach them either.
 *
 * `on-surface-variant` rather than `on-surface`, matching the prepend/append affordances above: an
 * affix qualifies the value, it is not part of it, and reading at the same weight as the number
 * would make "$" compete with the amount it labels.
 */
.hr-text-field :deep(.q-field__prefix),
.hr-text-field :deep(.q-field__suffix) {
  color: var(--app-on-surface-variant);
}

/* Disabled */
.hr-text-field.q-field--disabled :deep(.q-field__label),
.hr-text-field.q-field--disabled :deep(.q-field__native),
.hr-text-field.q-field--disabled :deep(.q-field__messages),
.hr-text-field.q-field--disabled :deep(.q-field__counter),
.hr-text-field.q-field--disabled :deep(.q-field__prepend),
.hr-text-field.q-field--disabled :deep(.q-field__append),
.hr-text-field.q-field--disabled :deep(.q-field__prefix),
.hr-text-field.q-field--disabled :deep(.q-field__suffix) {
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

/* ── Filled ─────────────────────────────────────────────────────────────── */

.hr-text-field--filled :deep(.q-field__control) {
  background: var(--app-surface-container-highest);
  border-radius: var(--app-shape-corner-extra-small) var(--app-shape-corner-extra-small) 0 0;
}

/*
 * ::before is Quasar's hover-state overlay + resting active-indicator line.
 * Reset Quasar's default grey background; keep only the M3 bottom border.
 */
.hr-text-field--filled :deep(.q-field__control::before) {
  background: transparent;
  border-bottom: 1px solid var(--app-on-surface-variant);
  border-radius: 0;
}

/* Hover state layer over the filled container */
.hr-text-field--filled :deep(.q-field__control:hover::before) {
  background: color-mix(in srgb, var(--app-on-surface) 8%, transparent);
}

/*
 * When focused, Quasar clears ::before so ::after (the animated indicator)
 * takes over. Explicitly confirm that here so our border-bottom doesn't bleed through.
 */
.hr-text-field--filled.q-field--focused :deep(.q-field__control::before),
.hr-text-field--filled.q-field--highlighted :deep(.q-field__control::before) {
  background: transparent;
  border-bottom-color: transparent;
}

/* ::after is Quasar's animated active-indicator line (scales in on focus) */
.hr-text-field--filled :deep(.q-field__control::after) {
  background: var(--app-primary);
}

/* Error: bottom indicator uses error color both at rest and when focused */
.hr-text-field--filled.q-field--error :deep(.q-field__control::before) {
  border-bottom-color: var(--app-error);
}

.hr-text-field--filled.q-field--error :deep(.q-field__control::after) {
  background: var(--app-error);
}

/* Disabled: dimmed container + dimmed indicator */
.hr-text-field--filled.q-field--disabled :deep(.q-field__control) {
  background: color-mix(in srgb, var(--app-on-surface) 4%, transparent);
}

.hr-text-field--filled.q-field--disabled :deep(.q-field__control::before) {
  border-bottom-color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

/* ── Outlined ───────────────────────────────────────────────────────────── */

.hr-text-field--outlined :deep(.q-field__control) {
  border-radius: var(--app-shape-corner-extra-small);
}

/* ::before is Quasar's border box for outlined */
.hr-text-field--outlined :deep(.q-field__control::before) {
  border: 1px solid var(--app-outline);
  border-radius: inherit;
}

/* Hover: border darkens to on-surface */
.hr-text-field--outlined :deep(.q-field__control:hover::before) {
  border-color: var(--app-on-surface);
}

/* Focused: 2 dp primary border */
.hr-text-field--outlined.q-field--focused :deep(.q-field__control::before) {
  border: 2px solid var(--app-primary);
}

/* Error: border uses error color */
.hr-text-field--outlined.q-field--error :deep(.q-field__control::before) {
  border-color: var(--app-error);
}

.hr-text-field--outlined.q-field--error.q-field--focused :deep(.q-field__control::before) {
  border: 2px solid var(--app-error);
}

/* Disabled: border fades to 12% on-surface */
.hr-text-field--outlined.q-field--disabled :deep(.q-field__control::before) {
  border-color: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
}

</style>
